// src/i18n.jsx
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { setHtmlDirection } from "./translations/utils/rtlHandler.jsx";
import { languageMeta } from "./translations/utils/languageMeta.jsx";
import { reportMissingKey } from "./translations/utils/missingKeys.jsx";

import { doc, setDoc, increment } from "firebase/firestore";
import { db } from "./firebase/firebase.jsx";

/* --------------------------- Config & helpers --------------------------- */
const USAGE_COLLECTION = "languageStats";
const DEV = Boolean(import.meta?.env?.DEV);
const PROD = Boolean(import.meta?.env?.PROD);

// List of supported UI languages (keys come from your languageMeta)
const SUPPORTED_LANGS = Object.keys(
    languageMeta || { en: { name: "English", dir: "ltr" } }
);

// Support BOTH JS and JSON translation bundles inside src/translations/
const jsBundles = import.meta.glob("./translations/*.js");
const jsonBundles = import.meta.glob("./translations/*.json");

// Track which langs we've loaded into i18n
const LOADED_LANGS = new Set();

/** Increment Firestore counter for language usage (prod only) */
async function incrementLanguageUsage(lang) {
    try {
        await setDoc(
            doc(db, USAGE_COLLECTION, lang),
            { count: increment(1) },
            { merge: true }
        );
    } catch (e) {
        console.warn(`⚠️ Failed to update usage for "${lang}"`, e);
    }
}

// Per session guard so we only count once per language
const incrementedThisSession = new Set();
function incrementOncePerSession(lang) {
    if (!PROD) return;
    if (!lang) return;

    const base = String(lang).split("-")[0];
    if (incrementedThisSession.has(base)) return;

    incrementLanguageUsage(base);
    incrementedThisSession.add(base);
}

function normalizeTranslationModule(mod) {
    const data = mod?.default || mod?.translation || mod || {};
    return data && typeof data === "object" ? data : {};
}

function getBundleLoader(langCode) {
    const candidates = [
        `./translations/${langCode}.js`,
        `./translations/${langCode}.json`,
    ];

    for (const path of candidates) {
        if (jsBundles[path]) return { loader: jsBundles[path], path };
        if (jsonBundles[path]) return { loader: jsonBundles[path], path };
    }

    return null;
}

function getLanguageCandidates(lang) {
    if (!lang) return [];
    const raw = String(lang).trim();
    if (!raw) return [];

    const base = raw.split("-")[0];
    return [...new Set([raw, base].filter(Boolean))];
}

/**
 * Try to load a translation bundle for a language code.
 * Returns the actual language key loaded into i18n, or null if not found.
 */
async function ensureLanguageLoaded(lang) {
    const candidates = getLanguageCandidates(lang);

    for (const cand of candidates) {
        if (LOADED_LANGS.has(cand) && i18n.hasResourceBundle(cand, "translation")) {
            return cand;
        }

        const match = getBundleLoader(cand);
        if (!match?.loader) continue;

        try {
            const mod = await match.loader();
            const data = normalizeTranslationModule(mod);

            if (data && typeof data === "object" && Object.keys(data).length > 0) {
                // Always replace with the latest loaded bundle in dev/runtime
                if (i18n.hasResourceBundle(cand, "translation")) {
                    i18n.removeResourceBundle(cand, "translation");
                }

                i18n.addResourceBundle(cand, "translation", data, true, true);
                LOADED_LANGS.add(cand);

                // Alias region code to base or base to region when needed
                if (lang && cand !== lang) {
                    if (i18n.hasResourceBundle(lang, "translation")) {
                        i18n.removeResourceBundle(lang, "translation");
                    }
                    i18n.addResourceBundle(lang, "translation", data, true, true);
                    LOADED_LANGS.add(lang);
                }

                if (DEV) {
                    console.log(`✅ Loaded translations for [${lang}] via ${match.path}`);
                }

                return cand;
            }
        } catch (err) {
            console.warn(
                `⚠️ Failed to load translations for ${cand} (${match.path})`,
                err
            );
        }
    }

    if (DEV) {
        console.warn(`⚠️ No translation bundle found for "${lang}"`);
    }

    return null;
}

/** Public helper to switch languages with lazy loading */
export async function loadLanguage(lang) {
    if (!lang) return;

    const requested = String(lang).trim();
    const base = requested.split("-")[0];
    const safeLang = SUPPORTED_LANGS.includes(base) ? base : "en";

    const loaded = await ensureLanguageLoaded(safeLang);
    const finalLang = loaded || "en";

    // Make sure fallback English always exists
    if (!i18n.hasResourceBundle("en", "translation")) {
        await ensureLanguageLoaded("en");
    }

    await i18n.changeLanguage(finalLang);
}

/** Convenience: persist + switch */
export async function setAppLanguage(lang) {
    const requested = String(lang || "").trim();
    const base = requested.split("-")[0];
    const safeLang = SUPPORTED_LANGS.includes(base) ? base : "en";

    try {
        localStorage.setItem("language", safeLang);
    } catch { }

    await loadLanguage(safeLang);
}

/* ------------------------------ i18n init ------------------------------ */
i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        // Start empty so every language, including English, uses the same dynamic load path
        resources: {},

        fallbackLng: "en",
        load: "languageOnly",
        supportedLngs: SUPPORTED_LANGS,
        nonExplicitSupportedLngs: true,
        debug: DEV,

        saveMissing: true,
        missingKeyHandler: (lng, ns, key) => {
            if (!key) return;
            try {
                reportMissingKey(lng || "en", key);
            } catch (e) {
                if (DEV) console.warn("reportMissingKey failed:", e);
            }
        },

        interpolation: { escapeValue: false },
        react: { useSuspense: true },

        // Honor only the user's stored choice
        detection: {
            order: ["localStorage"],
            caches: ["localStorage"],
            lookupLocalStorage: "language",
        },
    });

/* ---------------------------- language events --------------------------- */
i18n.on("languageChanged", async (lang) => {
    const base = String(lang || "en").split("-")[0];

    // Ensure selected language exists
    if (!i18n.hasResourceBundle(base, "translation")) {
        await ensureLanguageLoaded(base);
    }

    try {
        localStorage.setItem("language", base);
    } catch { }

    setHtmlDirection(base);
    incrementOncePerSession(base);

    const label = languageMeta?.[base]?.name || base;
    if (DEV) console.log(`✅ Language switched to: ${label}`);
});

/* --------------------------- initial boot logic ------------------------- */
// Default to explicit user choice (localStorage), else English.
const initialLang = (() => {
    try {
        const stored = localStorage.getItem("language");
        const base = (stored || "en").split("-")[0];
        return SUPPORTED_LANGS.includes(base) ? base : "en";
    } catch {
        return "en";
    }
})();

loadLanguage(initialLang).finally(() => {
    setHtmlDirection(initialLang);
    incrementOncePerSession(initialLang);
});

export default i18n;